// 站內通知（小鈴鐺）：一則通知要怎麼說、點了去哪裡、依日子分組、未讀數字。純函式（notifications.test.ts 有測）。
// 資料從 reviewApi.ts 的 listNotifications 來（後台 cf-worker/reviews.mjs 在事情發生時寫進去）。

import type { AppNotification } from './reviewApi';
import type { ProjectRole } from './mockData';
import { parseServerTime } from './serverTime';

const songOf = (n: AppNotification) => `「${n.songCode ? `${n.songCode} ` : ''}${n.songName || '一首歌'}」`;
const v = (n: AppNotification) => (n.versionLabel ? ` ${n.versionLabel}` : '');

/** 一句話說發生什麼事（不含專案名，畫面上另外寫） */
export function notificationTitle(n: AppNotification): string {
  const who = n.actorName || '有人';
  switch (n.kind) {
    case 'mention': return `${who} 在${songOf(n)}提到你`;
    case 'reply': return `${who} 回覆了你在${songOf(n)}的留言`;
    case 'needs_changes': return `${songOf(n)}${v(n)} 需修改（${who}）`;
    case 'approved': return `${songOf(n)}${v(n)} 已通過（${who}）`;
    case 'new_version': return `${songOf(n)}有新版${v(n)} 等你審${n.actorName ? `（${n.actorName} 交的）` : ''}`;
    case 'assigned': return `${who} 把${songOf(n)}指派給你`;
    // 分享連結的訪客留言：actorName = 「訪客：小李（給經紀人）」
    case 'guest_comment': return `${who} 在${songOf(n)}${v(n)} 留言`;
    // 導演貼連結交了一版（通知這首歌的動畫師和製作）
    case 'director_video': return `導演 ${who} 貼了一版影片${v(n)} 到${songOf(n)}`;
    // 導演在調整舞台存了 cue 的機關位置 / 另存 / 改轉場秒數：後台寫好整句（excerpt = 「改了 cue 30 的舞台（S05、S12 會一起變）」）
    case 'director_stage': return n.excerpt ? `導演 ${who} ${n.excerpt}` : `導演 ${who} 改了舞台`;
    // 這個專案的製作（和副手）收到的：動畫師交了新版 / 提交審查、別人留言（同一首歌沒讀的合併成一則，顯示最新的人和一句）
    case 'pm_new_version': return `${songOf(n)}有新版${v(n)}${n.actorName ? `（${n.actorName} 交的）` : ''}`;
    case 'pm_comment': return `${who} 在${songOf(n)}${v(n)} 留言`;
    // 製作用導演的名義留言 / 按了通過 / 需修改（標了「代導演」）：後台寫好整句（excerpt = 「代你按了需修改：S01 開場 V2」「代你留言（S01 開場 V2）：…」）
    case 'on_behalf': return n.excerpt ? `${who} ${n.excerpt}` : `${who} 用你的名義在${songOf(n)}做了一件事`;
    // 製作放上 / 換了 AE 打包檔：後台寫好整句（excerpt = 「換了新的 AE 打包檔（版型：全部）」）
    case 'ae_package': return `製作 ${who} ${n.excerpt || '換了 AE 打包檔'}，${songOf(n)}要用`;
    // 導演已確認的 cue 內容被改過、自動退回待確認：後台寫好整句（excerpt = 「cue 30 內容被改過，已退回「待確認」（S01、S02）」）
    case 'cue_returned': return n.excerpt || `${songOf(n)}有 cue 被退回待確認`;
    // 審查流程：放行（導演收）、轉交 / 退回 / 內部留言（動畫師收）
    case 'released': return `${songOf(n)}${v(n)} 可以看了${n.actorName ? `（${n.actorName} 放行）` : ''}`;
    case 'relayed': return `${who} 轉交了${songOf(n)}要改的地方`;
    case 'relayed_approved': return `${songOf(n)}通過了（${who} 轉告）`;
    case 'returned': return `${who} 請你繼續改${songOf(n)}`;
    case 'internal_comment': return `${who} 在${songOf(n)}${v(n)} 留言`;
    default: return `${songOf(n)}有新動態`;
  }
}

/** 第二行的小字（留言內容 / 需修改的原因 / 版本說明） */
export function notificationDetail(n: AppNotification): string {
  if (!n.excerpt) return '';
  if (n.kind === 'director_stage' || n.kind === 'on_behalf' || n.kind === 'ae_package' || n.kind === 'cue_returned') return '';   // 整句已經在標題裡
  if (n.kind === 'needs_changes') return `原因：${n.excerpt}`;
  return n.excerpt;
}

/** 這則通知說的那首歌最近被刪掉了：通知還在，但點了不會跳過去，改說一句白話（不是 = 空字串） */
export function notificationSongNote(n: Pick<AppNotification, 'songDeleted' | 'projectRole'>): string {
  if (!n.songDeleted) return '';
  return n.projectRole === 'admin'
    ? '這首歌已經刪除了，30 天內可以到編輯頁的「最近刪除的歌」救回來'
    : '這首歌已經刪除了，要救回來請找製作';
}

/** 點了開哪一頁：導演 / 製作 → 導演頁；動畫師 → 動畫師工作站。帶上歌、版本、留言 */
export function notificationHref(n: Pick<AppNotification, 'projectId' | 'projectRole' | 'songId' | 'videoId' | 'commentId'>): string {
  return songPageHref(n.projectRole === 'animator' ? 'studio' : 'preview', n.projectId, n);
}

/**
 * 導演頁（preview）/ 動畫師工作站（studio）直接打開某首歌、某一版、某則留言的網址：
 * /preview/<專案>?song=<歌>&video=<影片>&comment=<留言>（頁面用 parseDeepLink 讀）
 */
export function songPageHref(
  page: 'preview' | 'studio',
  projectId: string,
  target: { songId?: string | null; videoId?: string | null; commentId?: string | null },
): string {
  const q = new URLSearchParams();
  if (target.songId) q.set('song', target.songId);
  if (target.videoId) q.set('video', target.videoId);
  if (target.commentId) q.set('comment', target.commentId);
  const qs = q.toString();
  return `/${page}/${encodeURIComponent(projectId)}${qs ? `?${qs}` : ''}`;
}

/** 網址上的 ?song=&video=&comment=（通知點進來）→ 要打開哪首歌、哪一版、哪則留言 */
export interface DeepLink {
  songId: string | null;
  videoId: string | null;
  commentId: string | null;
}

const ID_RE = /^[a-zA-Z0-9_-]{1,64}$/;

export function parseDeepLink(search: string | URLSearchParams): DeepLink | null {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  const pick = (k: string) => {
    const x = q.get(k);
    return x && ID_RE.test(x) ? x : null;
  };
  const songId = pick('song');
  if (!songId) return null;
  return { songId, videoId: pick('video'), commentId: pick('comment') };
}

/** 角色 → 看這則通知用的頁面名字（「在導演頁打開」） */
export function pageLabel(role: ProjectRole | null): string {
  return role === 'animator' ? '動畫師工作站' : '導演頁';
}

/** 鈴鐺上的數字：0 不顯示，超過 99 寫 99+ */
export function badgeText(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return '';
  return count > 99 ? '99+' : String(Math.floor(count));
}

export interface NotificationGroup {
  label: string;
  items: AppNotification[];
}

function dayKey(ms: number, timeZone?: string): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone });  // 2026-10-14
}

/** 依日子分組（今天 / 昨天 / 更早），每組裡面最新的在前 */
export function groupNotifications(items: readonly AppNotification[], now = Date.now(), timeZone?: string): NotificationGroup[] {
  const today = dayKey(now, timeZone);
  const yesterday = dayKey(now - 86400_000, timeZone);
  const groups: NotificationGroup[] = [
    { label: '今天', items: [] },
    { label: '昨天', items: [] },
    { label: '更早', items: [] },
  ];
  const sorted = [...items].sort((a, b) => (parseServerTime(b.createdAt) || 0) - (parseServerTime(a.createdAt) || 0));
  for (const n of sorted) {
    const ms = parseServerTime(n.createdAt);
    const k = Number.isNaN(ms) ? '' : dayKey(ms, timeZone);
    if (k === today) groups[0].items.push(n);
    else if (k === yesterday) groups[1].items.push(n);
    else groups[2].items.push(n);
  }
  return groups.filter(g => g.items.length > 0);
}

/** 自動更新時：新舊清單一樣就不換（畫面不閃） */
export function sameNotifications(a: readonly AppNotification[], b: readonly AppNotification[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  // 合併過的留言通知 id 不變，但最新的人 / 一句 / 時間 / 留言換了 → 也要換（不然畫面上看不到新的）
  return a.every((x, i) => x.id === b[i].id && x.read === b[i].read && x.songName === b[i].songName && !!x.songDeleted === !!b[i].songDeleted
    && x.createdAt === b[i].createdAt && x.actorName === b[i].actorName && x.excerpt === b[i].excerpt && x.commentId === b[i].commentId);
}
